import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSettings } from "@/contexts/SettingsContext";
import { TrendingUp, Mail, Lock, Eye, EyeOff, Loader2, Check } from "lucide-react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";

export default function Login() {
  const { login, setUser } = useAuth();
  const { t } = useI18n();
  const { settings, loading: settingsLoading } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needCode, setNeedCode] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    if (!needCode && emailRef.current) {
      emailRef.current.focus();
    }
  }, [needCode]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor_required) {
        setNeedCode({ temp_token: data.temp_token });
      } else {
        if (data.access_token) localStorage.setItem("iq_token", data.access_token);
        setUser(data.user);
        nav(loc.state?.from?.pathname || "/trade");
      }
    } catch (er) { setError(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBusy(false); }
  };
  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/login", { temp_token: needCode.temp_token, code });
      if (data.access_token) localStorage.setItem("iq_token", data.access_token);
      setUser(data.user);
      nav(loc.state?.from?.pathname || "/trade");
    } catch (er) { setError(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-app grid-lines relative">
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(800px 500px at 50% -20%, rgba(0,230,118,0.08), transparent 70%), radial-gradient(600px 400px at 80% 80%, rgba(68,138,255,0.04), transparent 60%)"
        }}
      />
      <TopBar />
      <div className="relative z-10 flex items-start sm:items-center justify-center min-h-[calc(100vh-56px)] px-4 sm:px-4 pt-8 sm:pt-8 pb-16 sm:pb-10">
        <div className="w-full max-w-md mob-page" style={{ animation: "page-in 0.3s ease-out" }}>
          <div
            className="rounded-xl p-6 sm:p-8 border"
            style={{
              background: "var(--iq-panel-glass)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderColor: "var(--iq-border)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.03) inset",
            }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              {settingsLoading ? (
                <div className="w-9 h-9 rounded-lg bg-panel-hover shimmer" />
              ) : settings?.logo_base64 ? (
                <img src={settings.logo_base64} alt="Logo" className="max-h-9" />
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <TrendingUp className="w-5 h-5 text-black" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">{settings?.site_title || <>IQ<span className="text-buy">Trade</span></>}</span>
                </div>
              )}
            </div>

            <h1 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight">
              {needCode ? t("two_fa") : t("login")}
            </h1>
            {!needCode && (
              <p className="text-sm text-muted mb-6">Hesabına giriş yap ve işlem yapmaya başla</p>
            )}

            {!needCode ? (
              <form onSubmit={submitPassword} className="space-y-4" noValidate>
                {/* Email */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">{t("email")}</label>
                  <div
                    className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                    style={{
                      background: "var(--iq-bg)",
                      border: "1px solid var(--iq-border-bright)",
                    }}
                    onFocus={(e) => {
                      const parent = e.currentTarget;
                      parent.style.borderColor = "var(--iq-green)";
                      parent.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                    }}
                    onBlur={(e) => {
                      const parent = e.currentTarget;
                      parent.style.borderColor = "var(--iq-border-bright)";
                      parent.style.boxShadow = "none";
                    }}
                  >
                    <Mail className="w-4 h-4 text-dim shrink-0" />
                    <input
                      ref={emailRef}
                      data-testid="login-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">{t("password")}</label>
                  <div
                    className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                    style={{
                      background: "var(--iq-bg)",
                      border: "1px solid var(--iq-border-bright)",
                    }}
                    onFocus={(e) => {
                      const parent = e.currentTarget;
                      parent.style.borderColor = "var(--iq-green)";
                      parent.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                    }}
                    onBlur={(e) => {
                      const parent = e.currentTarget;
                      parent.style.borderColor = "var(--iq-border-bright)";
                      parent.style.boxShadow = "none";
                    }}
                  >
                    <Lock className="w-4 h-4 text-dim shrink-0" />
                    <input
                      data-testid="login-password"
                      type={showPw ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((p) => !p)}
                      className="text-dim hover:text-white transition-colors shrink-0 touch-target"
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember me */}
                <label className="flex items-center gap-2.5 cursor-pointer py-1 select-none">
                  <div
                    onClick={() => setRemember((r) => !r)}
                    className="w-4.5 h-4.5 rounded flex items-center justify-center transition-all duration-150"
                    style={{
                      width: 18,
                      height: 18,
                      background: remember ? "var(--iq-green)" : "var(--iq-bg)",
                      border: remember ? "1px solid var(--iq-green)" : "1px solid var(--iq-border-bright)",
                    }}
                  >
                    {remember && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                  </div>
                  <span className="text-xs text-muted">Beni Hatırla</span>
                </label>

                {error && (
                  <div data-testid="login-error" className="text-sm text-sell font-mono bg-sell-dim/20 border border-sell/20 rounded-lg px-3.5 py-2.5 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-sell shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="login-submit"
                  disabled={busy}
                  type="submit"
                  className="touch-target w-full font-bold py-3.5 sm:py-3 rounded-lg uppercase tracking-widest text-sm transition-all duration-200 disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, var(--iq-green) 0%, #00c853 100%)",
                    color: "#000",
                    boxShadow: busy ? "none" : "0 4px 20px rgba(0,230,118,0.25)",
                  }}
                  onMouseEnter={(e) => {
                    if (!busy) e.currentTarget.style.boxShadow = "0 6px 30px rgba(0,230,118,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    if (!busy) e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,230,118,0.25)";
                  }}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("login")}
                </button>
              </form>
            ) : (
              <form onSubmit={submitCode} className="space-y-4" noValidate>
                <p className="text-sm text-muted">{t("two_fa_setup_hint")}</p>
                <input
                  data-testid="login-2fa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-app border border-border-bright rounded-lg px-3 py-4 outline-none font-mono text-center tracking-[0.3em] text-2xl transition-all duration-200"
                  style={{ borderColor: "var(--iq-border-bright)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-green)";
                    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-border-bright)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
                {error && (
                  <div data-testid="login-2fa-error" className="text-sm text-sell font-mono bg-sell-dim/20 border border-sell/20 rounded-lg px-3.5 py-2.5 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-sell shrink-0" />
                    {error}
                  </div>
                )}
                <button
                  data-testid="login-2fa-submit"
                  disabled={busy || code.length !== 6}
                  type="submit"
                  className="touch-target w-full font-bold py-3.5 sm:py-3 rounded-lg uppercase tracking-widest text-sm transition-all duration-200 disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, var(--iq-green) 0%, #00c853 100%)",
                    color: "#000",
                    boxShadow: "0 4px 20px rgba(0,230,118,0.25)",
                  }}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("submit")}
                </button>
                <button
                  type="button"
                  onClick={() => { setNeedCode(null); setCode(""); }}
                  className="touch-target w-full text-sm text-muted hover:text-white py-3 transition-colors"
                >
                  {t("cancel")}
                </button>
              </form>
            )}

            {/* Footer links */}
            <div className="mt-7 text-sm text-muted text-center space-y-3">
              <div>
                <Link to="/forgot-password" className="text-buy hover:underline hover:text-emerald-300 transition-colors inline-block py-1">Şifremi unuttum</Link>
              </div>
              <div className="border-t border-line/50 pt-3">
                {t("no_account")}{" "}
                <Link to="/register" className="text-buy hover:underline hover:text-emerald-300 transition-colors font-semibold inline-block py-1" data-testid="to-register">
                  {t("register")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
